import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { renderSVG } from 'uqr';
import { z } from 'zod';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk, ME_KEY, type Profile } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { TextField } from '../../components/ui/text-field';
import { useI18n } from '../../i18n/i18n';

const STATUS_KEY = ['me', '2fa'] as const;
const codeSchema = z.object({ code: z.string().trim().min(6, 'validation.code') });
type CodeValues = z.infer<typeof codeSchema>;

/** 2FA: включение по QR и первому коду, коды восстановления, отключение. */
export function TwoFactorSection({ profile }: { profile: Profile }) {
  const { t } = useI18n();
  const status = useQuery({
    queryKey: STATUS_KEY,
    queryFn: async () => unwrap(await api.GET('/api/v1/me/2fa')),
  });
  // Коды восстановления показываются один раз — живут только в состоянии страницы.
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);

  return (
    <Card title={t('profile.2fa.title')}>
      {recoveryCodes !== null && (
        <RecoveryCodes codes={recoveryCodes} onDone={() => setRecoveryCodes(null)} />
      )}
      {status.data?.enabled === true ? (
        <EnabledTwoFactor
          hasPassword={profile.hasPassword}
          remaining={status.data.recoveryCodesRemaining}
          onCodes={setRecoveryCodes}
        />
      ) : status.data?.enabled === false ? (
        <SetupTwoFactor hasPassword={profile.hasPassword} onCodes={setRecoveryCodes} />
      ) : null}
    </Card>
  );
}

function SetupTwoFactor({
  hasPassword,
  onCodes,
}: {
  hasPassword: boolean;
  onCodes: (codes: string[]) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const form = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });
  const setupSchema = useMemo(
    () =>
      z.object({ password: hasPassword ? z.string().min(1, 'validation.required') : z.string() }),
    [hasPassword],
  );
  const passwordForm = useForm<{ password: string }>({
    resolver: zodResolver(setupSchema),
    defaultValues: { password: '' },
  });
  // Настройка начинается с пароля: одной сессии для смены способов входа мало (ADR-0013).
  const setup = useMutation({
    mutationFn: async (password: string) =>
      unwrap(
        await api.POST('/api/v1/me/2fa/setup', {
          body: password === '' ? {} : { password },
        }),
      ),
  });
  const enable = useMutation({
    mutationFn: async (body: CodeValues) =>
      unwrap(await api.POST('/api/v1/me/2fa/enable', { body })),
    onSuccess: async (result) => {
      onCodes(result.recoveryCodes);
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
    },
  });
  const qr = useMemo(
    () =>
      setup.data === undefined
        ? null
        : `data:image/svg+xml;utf8,${encodeURIComponent(renderSVG(setup.data.otpauthUri))}`,
    [setup.data],
  );

  if (setup.data === undefined) {
    return (
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{t('profile.2fa.off')}</p>
        <form
          className="space-y-3"
          onSubmit={passwordForm.handleSubmit((values) => setup.mutate(values.password))}
          noValidate
        >
          {hasPassword && (
            <TextField
              control={passwordForm.control}
              name="password"
              type="password"
              label={t('auth.field.currentPassword')}
              autoComplete="current-password"
            />
          )}
          {setup.isError && <Notice tone="danger">{t(errorMessageKey(setup.error))}</Notice>}
          <Button type="submit" variant="secondary" disabled={setup.isPending}>
            {t('profile.2fa.enable')}
          </Button>
        </form>
      </div>
    );
  }
  return (
    <form
      className="space-y-3"
      onSubmit={form.handleSubmit((values) => enable.mutate(values))}
      noValidate
    >
      <p className="text-sm">{t('profile.2fa.scan')}</p>
      {qr !== null && (
        <img
          src={qr}
          alt={t('profile.2fa.qrAlt')}
          className="size-44 rounded-sm border border-border bg-white p-2"
        />
      )}
      <p className="text-2xs text-muted-foreground">
        {t('profile.2fa.manual')} <code className="font-mono">{setup.data.secret}</code>
      </p>
      <TextField
        control={form.control}
        name="code"
        label={t('auth.field.code')}
        autoComplete="one-time-code"
        inputMode="numeric"
      />
      {enable.isError && <Notice tone="danger">{t(errorMessageKey(enable.error))}</Notice>}
      <Button type="submit" disabled={enable.isPending}>
        {t('profile.2fa.confirm')}
      </Button>
    </form>
  );
}

const disableSchema = z.object({
  password: z.string(),
  code: z.string().trim().min(6, 'validation.code'),
});
type DisableValues = z.infer<typeof disableSchema>;

function EnabledTwoFactor({
  hasPassword,
  remaining,
  onCodes,
}: {
  hasPassword: boolean;
  remaining: number;
  onCodes: (codes: string[]) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const disableForm = useForm<DisableValues>({
    resolver: zodResolver(disableSchema),
    defaultValues: { password: '', code: '' },
  });
  const regenerateForm = useForm<CodeValues>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: '' },
  });

  const disable = useMutation({
    mutationFn: async (values: DisableValues) =>
      ensureOk(
        await api.POST('/api/v1/me/2fa/disable', {
          body: { code: values.code, ...(hasPassword ? { password: values.password } : {}) },
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
  const regenerate = useMutation({
    mutationFn: async (body: CodeValues) =>
      unwrap(await api.POST('/api/v1/me/2fa/recovery-codes', { body })),
    onSuccess: async (result) => {
      onCodes(result.recoveryCodes);
      regenerateForm.reset();
      await queryClient.invalidateQueries({ queryKey: STATUS_KEY });
    },
  });

  return (
    <div className="space-y-5">
      <Notice tone="success">
        {t('profile.2fa.on')} {t('profile.2fa.remaining')} {remaining}
      </Notice>
      <form
        className="space-y-3"
        onSubmit={regenerateForm.handleSubmit((values) => regenerate.mutate(values))}
        noValidate
      >
        <h3 className="text-sm font-medium">{t('profile.2fa.regenerate')}</h3>
        <TextField
          control={regenerateForm.control}
          name="code"
          label={t('profile.2fa.appCode')}
          autoComplete="one-time-code"
          inputMode="numeric"
        />
        {regenerate.isError && (
          <Notice tone="danger">{t(errorMessageKey(regenerate.error))}</Notice>
        )}
        <Button type="submit" variant="secondary" disabled={regenerate.isPending}>
          {t('profile.2fa.regenerateSubmit')}
        </Button>
      </form>
      <form
        className="space-y-3"
        onSubmit={disableForm.handleSubmit((values) => disable.mutate(values))}
        noValidate
      >
        <h3 className="text-sm font-medium">{t('profile.2fa.disable')}</h3>
        {hasPassword && (
          <TextField
            control={disableForm.control}
            name="password"
            type="password"
            label={t('auth.field.currentPassword')}
            autoComplete="current-password"
          />
        )}
        <TextField
          control={disableForm.control}
          name="code"
          label={t('auth.field.code')}
          autoComplete="one-time-code"
        />
        {disable.isError && <Notice tone="danger">{t(errorMessageKey(disable.error))}</Notice>}
        <Button type="submit" variant="danger" disabled={disable.isPending}>
          {t('profile.2fa.disableSubmit')}
        </Button>
      </form>
    </div>
  );
}

function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useI18n();
  return (
    <div className="space-y-3 rounded-sm border border-warning p-4">
      <p className="text-sm font-medium">{t('profile.2fa.codesTitle')}</p>
      <p className="text-2xs text-muted-foreground">{t('profile.2fa.codesHint')}</p>
      <ul className="grid grid-cols-2 gap-1 font-mono text-sm">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button type="button" variant="secondary" onClick={onDone}>
        {t('profile.2fa.codesSaved')}
      </Button>
    </div>
  );
}
